<template>
  <div class="portfolio-container">
    <div v-if="isLoading && uuid == null" class="placeholder">Portfolio is not selected!</div>
    <div v-if="isLoading && uuid !== null" class="placeholder">Loading Data...</div>
    <div v-if="!isLoading && rawData === null" class="placeholder">⚠️ Failed to load data</div>
    <div v-if="!isLoading && rawData !== null" class="portfolio-content">
      <div class="top-buttons">
        <!-- eslint-disable-next-line max-len -->
        <button
          @click="toggleViewsDisplay"
          v-if="isAdmin && !displayCoins"
          class="button button-sm"
        >
          <BIconEyeFill /> <span class="is-hidden">{{ displayViewsText }} Views</span>
        </button>
      </div>
      <div v-if="updatesInSeconds > 0" class="next-update">Next update in {{ updatesInSeconds }} seconds</div>
      <div class="portfolio-block">
        <div v-if="!displayCoins" class="title">
          <span v-if="displayBalance">{{ title }} balance</span>
          <span v-else>{{ rawData.title }} invested</span>
        </div>
        <div v-if="!displayCoins" class="total-balance">
          <!-- eslint-disable-next-line max-len -->
          <!-- eslint-disable-next-line max-len, vuejs-accessibility/click-events-have-key-events -->
          <span @click="toggleBalanceDisplay">
            <span v-if="displayBalance">{{ displayCoinPrice(portfolioBalance) }}</span>
            <span v-else>{{ displayCoinPrice(rawData.balance.invested) }}</span>
          </span>
        </div>
        <div
          v-if="!displayCoins"
          class="change-block"
          v-bind:class="{
            profit: rawData.stats.profit.amount > 0,
            loss: rawData.stats.profit.amount < 0,
          }"
        >
          <!-- eslint-disable-next-line max-len -->
          <div class="percentage">
            {{ parseFloat(rawData.stats.profit.percentage).toFixed(2) }}%
          </div>
          <!-- eslint-disable-next-line max-len -->
          <div class="amount">
            {{ displayCoinPrice(parseInt(Math.abs(rawData.stats.profit.amount).toFixed(2)), true) }}
          </div>
        </div>
        <div v-if="!portfolioUsesShares" class="buttons" :class="{ 'fixed-width': displayCoins }">
          <button @click="toggleCoinsDisplay" class="button">
            <BIconListUl /> <span>{{ displayCoinsText }} List</span>
          </button>
          <button @click="toggleChartDisplay" v-if="!displayCoins" class="button">
            <BIconPieChartFill /> <span>{{ displayChartText }} Chart</span>
          </button>
        </div>
        <!-- eslint-disable-next-line max-len -->
        <apexchart
          v-if="!displayCoins && !portfolioUsesShares"
          class="chart"
          :width="chart.width"
          :type="chart.type"
          :options="chart.options"
          :series="chart.series"
          :class="{
            'is-offstage': !displayChart,
          }"
        ></apexchart>
        <div
          class="coins"
          :class="{
            'is-hidden': !displayCoins,
          }"
        >
          <div class="title">Last 24h performance</div>
          <div v-for="row in portfolioCoins" :key="row.id" class="row">
            <img
              :src="`https://s2.coinmarketcap.com/static/img/coins/16x16/${row.id}.png`"
              widt="16"
              height="16"
              :alt="row.ticker"
            />
            <span class="name">{{ row.name }}</span>
            <span class="ticker">{{ row.ticker }}</span>
            <span class="price">{{ displayCoinPrice(row.price, false, true, row.ticker) }}</span>
            <span
              class="change"
              :class="{
                'is-positive': row.change_1d >= 0,
                'is-negative': row.change_1d < 0,
                // eslint-disable-next-line max-len
              }"
              ><small>{{ row.change_1d >= 0 ? "▲" : "▼" }}</small
              >{{ Math.abs(row.change_1d) }}%</span
            >
          </div>
        </div>
        <div v-if="displayViews && !displayCoins" class="views">
          <span class="title">Today Views ({{ portfolioViews.length || 0 }})</span>
          <div class="items">
            <span v-for="viewItem in portfolioViews" :key="viewItem.id" class="date">
              {{ viewItem.time }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { randomColor } from "randomcolor";
import {
  ref,
  watch,
  onMounted,
  computed,
} from "vue";
import { useHead } from "@vueuse/head";
import { useRoute } from "vue-router";
import { useCookie } from "vue-cookie-next";
import { useStorage } from "vue3-storage";
import VueApexCharts from "vue3-apexcharts";
import { BIconEyeFill, BIconListUl, BIconPieChartFill } from "bootstrap-icons-vue";

export default {
  components: {
    apexchart: VueApexCharts,
    BIconEyeFill,
    BIconListUl,
    BIconPieChartFill,
  },
  setup() {
    const route = useRoute();
    const cookie = useCookie();
    const localStorage = useStorage();

    const storageDataLifetimeSeconds = 365 * 24 * (60 * 60 * 1000); // 365 days

    // eslint-disable-next-line max-len
    const isAdmin = ref(
      cookie.isCookieAvailable("auth-token") &&
        cookie.getCookie("auth-token") === process.env.VUE_APP_ADMIN_TOKEN
    );
    // const isAdmin = ref(true);
    const isLoading = ref(true);
    const uuid = ref(route.params.uuid);
    const countdownIntervalId = ref(-1);
    const updatesInSeconds = ref(-1);

    // do not count this portfolio view
    const isSneaky = route.query.sneaky !== undefined;
    // const isSneaky = true;

    // user color scheme
    const usersPrefersDarkScheme =
      window.matchMedia("(prefers-color-scheme: dark)").matches || false;
    const darkThemeBgColor = "#121212";
    const darkThemeTextColor = "rgb(201, 195, 195)";
    const lightThemeBgColor = "#fafafa";
    const lightThemeTextColor = "#2a2a2a";
    const userBgColor = usersPrefersDarkScheme ? darkThemeBgColor : lightThemeBgColor;
    // eslint-disable-next-line no-unused-vars
    const userTextColor = usersPrefersDarkScheme ? darkThemeTextColor : lightThemeTextColor;

    // portfolio variables
    const title = ref("");
    const rawData = ref({});
    const chart = ref({
      background: "transparent",
      series: [],
      options: {},
      width: 300,
      type: "pie",
    });
    const portfolioBalance = ref(0);
    const portfolioCoins = ref({});
    const portfolioProfit = ref(0);
    const portfolioProfitPercentage = ref(0);
    const portfolioViews = ref({});
    const portfolioUsesShares = ref(false); // if true, then coins are not displayed
    const displayBalance = ref(true);
    const displayProfit = ref(true);
    const displayChart = ref(localStorage.getStorageSync("settings_show_chart") || false);
    const displayChartText = ref(displayChart.value === true ? "Hide" : "Show");
    const displayCoins = ref(false);
    const displayCoinsText = ref(displayCoins.value === true ? "Hide" : "Show");
    const displayViews = ref(localStorage.getStorageSync("settings_show_views") || false);
    const displayViewsText = ref(displayViews.value === true ? "Hide" : "Show");
    const cryptoApiBaseUri = process.env.VUE_APP_API_BASE_URI ?? 'api.cryptorian.ru';

    const fetchJsonWithTimeout = async (url, timeoutMs = 2000) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        return await response.json();
      } finally {
        clearTimeout(timeoutId);
      }
    };

    const displayCoinPrice = (value, shortFormat = false, autoDecimals = false, debug = "") => {
      if (shortFormat) {
        let newValue = value;
        const suffixes = ["", "K", "M", "B", "T"];
        let suffixNum = 0;
        while (newValue >= 1000) {
          newValue /= 1000;
          suffixNum += 1;
        }
        newValue = newValue.toPrecision(3);
        if (newValue.endsWith(".0")) {
          newValue = newValue.slice(0, -2);
        }

        return `$${newValue}${suffixes[suffixNum]}`;
      }

      let decimals = 0;
      if (autoDecimals) {
        // eslint-disable-next-line max-len
        const weight = Math.abs(
          ((parseFloat(value).toFixed(2) - parseFloat(value).toFixed(0)) /
            parseFloat(value).toFixed(2)) *
            100
        );
        if (weight >= 0.1) {
          decimals = 2;
        }
      }

      return new Intl.NumberFormat("en-EN", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: decimals,
      }).format(value);
    };

    const countdown = () => {
      countdownIntervalId.value = setInterval(() => {
        updatesInSeconds.value -= 1;
      }, 1000);
    };

    const fetchBalance = async () => {
      if (uuid.value.length === 0) {
        return;
      }

      try {
        const responseData = await fetchJsonWithTimeout(
          `https://${cryptoApiBaseUri}/v1/portfolio?uuid=${uuid.value}${isSneaky ? "&sneaky" : ""}`,
          2000
        );
        const { data } = responseData;

        data.balance.fixM = data.balance.real / data.balance.actual;

        const dayProfitIsNegative = data.stats.balance.change["24h"].usd_value < 0;
        data.stats.balance.change["24h"].isNegative = dayProfitIsNegative;
        data.stats.balance.change["24h"].usdValuePrefix = dayProfitIsNegative ? "-" : "+";

        // profit
        if (data.info.hide_profit === true) {
          data.stats.profit.amount = 0;
          data.stats.profit.percentage = 0;
          displayProfit.value = false;
        }

        // uses shares
        if (data.info.uses_shares === true) {
          portfolioUsesShares.value = true;
        }

        // chart
        const rowsPrepared = {};
        const rowsFinal = {};
        if (!portfolioUsesShares.value) {
          // eslint-disable-next-line no-restricted-syntax
          for (const row of data.rows) {
            rowsPrepared[row.id] = row.portfolio_balance;
            rowsFinal[row.id] = {
              name: `${row.name} (${row.ticker})`,
              balance: parseFloat(row.portfolio_balance),
              hexColor: row.hex_color !== "" ? `#${row.hex_color}` : "",
              isLocked: row.is_locked,
            };
          }
        }
        const chartSeries = [];
        const chartLabels = [];
        const chartColors = [];
        if (!portfolioUsesShares.value) {
          // eslint-disable-next-line max-len
          const rowsKeysSorted = Object.keys(rowsPrepared).sort(
            (a, b) => rowsPrepared[b] - rowsPrepared[a]
          );
          // eslint-disable-next-line no-restricted-syntax
          for (const key of rowsKeysSorted) {
            const row = rowsFinal[key];
            chartSeries.push(row.balance);
            chartLabels.push(row.name);
            chartColors.push(
              row.isLocked
                ? "rgba(106, 108, 129, 0.9)"
                : row.hexColor ||
                    randomColor({
                      luminosity: "dark",
                      format: "rgba",
                      alpha: 0.9,
                    })
            );
          }
        }

        // update in
        const currentDate = new Date();
        const currentMinutes = currentDate.getMinutes();
        const currentSeconds = currentDate.getSeconds();
        const updateDelaySeconds = 30;
        // eslint-disable-next-line max-len
        const updateInMinutes =
          data.info.update_interval - (currentMinutes % data.info.update_interval);
        const updateInSeconds = parseInt(updateInMinutes * 60 + (updateDelaySeconds - currentSeconds));

        // balance
        const totalBalance = parseInt(data.balance.total, 10);
        // const investedValue = parseInt(data.balance.invested, 10);

        chart.value.series = chartSeries;
        chart.value.options = {
          chart: {
            background: "transparent",
          },
          theme: {
            mode: "light",
            background: "transparent",
          },
          stroke: {
            colors: [userBgColor],
          },
          // title: {
          //   text: 'Overview',
          //   align: 'center',
          //   style: {
          //     color: userTextColor,
          //   },
          // },
          labels: chartLabels,
          colors: chartColors,
          legend: {
            show: false,
          },
          responsive: [
            {
              breakpoint: 480,
              options: {
                chart: {
                  width: 400,
                },
              },
            },
          ],
          yaxis: {
            labels: {
              formatter(value) {
                return displayCoinPrice(value);
              },
            },
          },
        };
        rawData.value = data;
        title.value = data.title !== undefined ? `${data.title}'s` : "";
        isLoading.value = false;
        portfolioBalance.value = totalBalance;
        // (totalBalance > investedValue) ? totalBalance : investedValue;
        // coins (sorted by name)
        portfolioCoins.value = data.rows.sort((a, b) => {
          if (a.name.toLowerCase() === b.name.toLowerCase()) {
            return 0;
          }
          if (a.name.toLowerCase() > b.name.toLowerCase()) {
            return 1;
          }
          return -1;
        });
        portfolioProfit.value = data.stats.profit.amount;
        portfolioProfitPercentage.value = data.stats.profit.percentage;
        updatesInSeconds.value = updateInSeconds;
        countdown();
      } catch (error) {
        isLoading.value = false;
      }
    };

    const fetchViews = async () => {
      try {
        const responseData = await fetchJsonWithTimeout(
          `https://${cryptoApiBaseUri}/v1/portfolio-views?uuid=${uuid.value}&token=${process.env.VUE_APP_ADMIN_ACCESS_TOKEN}&filter=today`,
          2000
        );
        // eslint-disable-next-line no-prototype-builtins
        if (responseData.data !== undefined) {
          const { data } = responseData;
          portfolioViews.value = data.rows;
        }
      } catch (err) {
        console.error(err);
      }
    };

    const toggleBalanceDisplay = () => {
      if (!displayProfit.value) {
        return;
      }
      displayBalance.value = !displayBalance.value;
    };

    const toggleChartDisplay = () => {
      displayChart.value = !displayChart.value;
      displayChartText.value = displayChart.value ? "Hide" : "Show";
      localStorage.setStorageSync(
        "settings_show_chart",
        displayChart.value,
        storageDataLifetimeSeconds
      );
    };

    const toggleCoinsDisplay = () => {
      displayCoins.value = !displayCoins.value;
      displayCoinsText.value = displayCoins.value ? "Hide" : "Show";
    };

    const toggleViewsDisplay = () => {
      displayViews.value = !displayViews.value;
      displayViewsText.value = displayViews.value ? "Hide" : "Show";
      localStorage.setStorageSync(
        "settings_show_views",
        displayViews.value,
        storageDataLifetimeSeconds
      );
    };

    useHead({
      title: computed(() => `${title.value} Crypto Portfolio`),
    });

    onMounted(() => {
      fetchBalance();
      if (isAdmin.value) {
        fetchViews();
      }
    });

    // eslint-disable-next-line no-unused-vars
    watch(updatesInSeconds, (newValue, oldValue) => {
      if (newValue === 0) {
        if (countdownIntervalId.value > 0) {
          clearInterval(countdownIntervalId.value);
          fetchBalance();
        }
      }
    });

    return {
      displayBalance,
      displayCoinPrice,
      toggleBalanceDisplay,
      toggleChartDisplay,
      toggleCoinsDisplay,
      toggleViewsDisplay,
      uuid,
      isLoading,
      isAdmin,
      rawData,
      title,
      portfolioBalance,
      portfolioCoins,
      portfolioProfit,
      portfolioProfitPercentage,
      portfolioViews,
      portfolioUsesShares,
      chart,
      displayChart,
      displayChartText,
      displayCoins,
      displayCoinsText,
      displayProfit,
      displayViews,
      displayViewsText,
      updatesInSeconds,
    };
  },
};
</script>

<style lang="scss" scoped>
.button {
  color: var(--text-color);

  background: transparent;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:not(.button-sm),
  &.button-md {
    // default button
    padding: 0 10px;
    line-height: 30px;

    font-size: 14px;

    > svg {
      margin-top: 8px;
    }
  }

  &.button-sm {
    padding: 0 5px;
    line-height: 18px;

    font-size: 12px;

    > svg {
      margin-top: 3px;
    }
  }

  &:hover {
    color: #2a2a2a;

    background-color: var(--button-bg-color);
  }

  > span {
    margin-left: 4px;

    font-size: 14px;
    font-weight: 700;
    vertical-align: top;
  }

  > svg {
    vertical-align: top;
  }
}

.button + .button {
  margin-left: 5px;
}

.portfolio-container,
.portfolio-content,
.portfolio-content > div:not([class]),
.portfolio-content > div:not([class]) > div:not([class]),
.portfolio-block {
  height: 100%;
}

.portfolio-block {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  /* height: 200px; */
  cursor: default;
  user-select: none;

  > .title {
    margin-bottom: 8px;

    color: var(--gray-text-color);
    font-size: 16px;
  }

  > .total-balance {
    color: var(--text-color);
    font-size: 55px;
  }

  > .change-block {
    margin-top: 15px;

    display: table;

    &.loss {
      > .amount {
        color: var(--red-text-color);
      }

      > .percentage {
        background-color: var(--red-text-color);
      }
    }

    &.profit {
      > .amount {
        color: var(--green-text-color);
      }

      > .percentage {
        background-color: var(--green-text-color);
      }
    }

    > div {
      display: table-cell;
      vertical-align: top;
    }

    > .percentage {
      padding: 3px 5px;

      color: #fff;
      font-size: 14px;
      font-weight: 700;

      border-radius: 3px;
    }

    > .amount {
      padding-top: 3px;
      padding-left: 10px;

      color: var(--green-text-color);
      font-size: 14px;
      font-weight: 700;
    }
  }

  > .info-balance {
    margin-top: 7px;

    color: #9c9c9c;
    font-size: 24px;

    > .profit-balance {
      color: #1d8d1d;
    }

    > .loss-balance {
      color: rgb(224 65 65);
    }
  }

  > .buttons {
    margin-top: 20px;

    &.fixed-width {
      width: 200px;

      text-align: left;
    }
  }

  > .chart {
    margin-top: 10px;

    &.is-offstage {
      visibility: hidden;
      position: absolute;
      left: -9999px;
      top: 0;
      pointer-events: none;
    }
  }

  > .coins {
    margin: 10px auto 0;

    width: 230px;

    text-align: left;

    > .title {
      margin-bottom: 25px;

      padding-top: 10px;

      color: var(--text-color);
      font-size: 12px;
      font-weight: 700;
    }

    > .row {
      display: block;

      height: 16px;

      font-size: 12px;

      // border: 1px solid #eee;

      > img {
        border-radius: 50%;
      }

      > span {
        vertical-align: top;

        line-height: 16px;
      }

      > .name {
        margin-left: 5px;

        color: var(--text-color);
      }

      > .ticker {
        margin-left: 3px;

        color: var(--gray-text-color);
      }

      > .price {
        margin-left: 3px;

        color: var(--text-color);
      }

      > .change {
        margin-left: 3px;

        &.is-positive {
          color: var(--green-text-color);
        }

        &.is-negative {
          color: var(--red-text-color);
        }

        > small {
          vertical-align: top;
          margin-right: 1px;
        }
      }
    }

    > .row + .row {
      margin-top: 10px;
    }
  }

  > .views {
    margin-top: 15px;

    width: 200px;

    text-align: center;

    > .title {
      display: inline-block;
      margin-bottom: 6px;

      color: var(--text-color);
      font-size: 14px;
      font-weight: 700;
      opacity: 0.75;
    }

    > .items {
      > .date {
        display: inline-block;
        padding: 0 2px;

        color: #999; //var(--text-color);
        font-size: 11px;
        font-weight: 500;
      }
    }
  }
}

.loader {
  margin: 0 auto;

  width: 200px;

  font-size: 20px;
  text-align: center;
}

.next-update {
  position: absolute;
  top: 10px;
  right: 10px;

  color: #9a9a9a;
  font-size: 11px;
}

.top-buttons {
  position: absolute;
  top: 10px;
  left: 10px;
}

.placeholder {
  padding-top: 50px;

  color: var(--text-color);
  font-size: 20px;
  text-align: center;
}
</style>
